import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  FileSpreadsheet,
  Loader2,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import OneDriveSync from "@/components/import/OneDriveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  createProperty,
  importContractors,
  importExpenses,
  importInsurancePolicies,
  importTenants,
  importVaultAccounts,
  listContractors,
  listExpenses,
  listInsurancePolicies,
  listProperties,
  listTenants,
  listVaultAccounts,
} from "@/services/dataService";
import {
  FIELD_SPECS,
  KIND_DESTINATIONS,
  KIND_LABELS,
  TAB_LABELS,
  buildImportPayload,
  parseCsvWithLines,
  parseWorkbookRows,
  sheetRowsToLines,
  planRows,
  propertiesToCreate,
} from "@/lib/workbookImport";

/**
 * Spreadsheet import. CSV files are read in the browser; parsed rows (including
 * passwords) only live in this page's memory and are sent to the owner's
 * account when they press Import. Leaving the page discards them.
 */

const KIND_ORDER = ["contractors", "insurance", "expenses", "tenants", "vault"];
const BATCH_SIZE = 200;

const IMPORTERS = {
  contractors: importContractors,
  expenses: importExpenses,
  insurance: importInsurancePolicies,
  tenants: importTenants,
  vault: importVaultAccounts,
};

const LOADERS = {
  contractors: () => listContractors(),
  expenses: () => listExpenses(),
  insurance: () => listInsurancePolicies(),
  tenants: () => listTenants(),
  vault: () => listVaultAccounts(),
};

const STATUS = {
  new: { label: "New", className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
  update: { label: "Update", className: "bg-sky-500/15 text-sky-700 dark:text-sky-300" },
  same: { label: "Already there", className: "bg-muted text-muted-foreground" },
  invalid: { label: "Needs info", className: "bg-amber-500/15 text-amber-800 dark:text-amber-300" },
};

const PROPERTY_HINT = {
  link: (property) => `Linked to ${property.label}`,
  create: (property) => `Adds ${property.label} to Properties`,
  missing: (property) => `${property.label} isn't in Properties yet`,
};

const isImportable = (plan) => plan.status === "new" || plan.status === "update";

function chunk(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

async function runImporter(kind, payload) {
  const totals = { created: 0, updated: 0 };
  const batches = [
    ...chunk(payload.creates, BATCH_SIZE).map((creates) => ({ creates, updates: [] })),
    ...chunk(payload.updates, BATCH_SIZE).map((updates) => ({ creates: [], updates })),
  ];
  for (const batch of batches) {
    const result = await IMPORTERS[kind](batch);
    totals.created += result.created;
    totals.updated += result.updated;
  }
  return totals;
}

let fileSeq = 0;

export default function Import() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const inputRef = useRef(null);

  const [files, setFiles] = useState([]); // { id, name, label, rows, forcedTab }
  const [edits, setEdits] = useState({}); // rowId → { field: value }
  const [selection, setSelection] = useState({}); // rowId → boolean (owner overrides)
  const [showSecrets, setShowSecrets] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [importError, setImportError] = useState("");

  const parsedFiles = useMemo(
    () =>
      files.map((file) => {
        const parsed = parseWorkbookRows(file.label, file.rows, file.forcedTab);
        return { ...file, ...parsed };
      }),
    [files],
  );

  const groups = useMemo(() => {
    const byKind = {};
    for (const file of parsedFiles) {
      for (const section of file.sections) {
        const group = (byKind[section.kind] ||= { kind: section.kind, rows: [], skipped: [] });
        for (const row of section.rows) {
          const id = `${file.id}:${row.id}`;
          group.rows.push({ ...row, id, fileName: file.name, data: { ...row.data, ...edits[id] } });
        }
        for (const skipped of section.skipped) {
          group.skipped.push({ ...skipped, fileName: file.name });
        }
      }
    }
    return KIND_ORDER.filter((kind) => byKind[kind]).map((kind) => byKind[kind]);
  }, [parsedFiles, edits]);

  const kindsPresent = new Set(groups.map((group) => group.kind));
  const needsProperties = groups.some((group) => ["expenses", "insurance", "tenants"].includes(group.kind));

  const existingResults = useQueries({
    queries: KIND_ORDER.map((kind) => ({
      queryKey: ["workbookImport", kind, userId],
      queryFn: LOADERS[kind],
      enabled: Boolean(userId) && kindsPresent.has(kind),
    })),
  });
  const existingQueries = Object.fromEntries(KIND_ORDER.map((kind, index) => [kind, existingResults[index]]));
  const propertiesQuery = useQuery({
    queryKey: ["workbookImport", "properties", userId],
    queryFn: () => listProperties(),
    enabled: Boolean(userId) && needsProperties,
  });

  const loadingExisting =
    groups.some((group) => !existingQueries[group.kind].isSuccess) ||
    (needsProperties && !propertiesQuery.isSuccess);
  const loadError =
    groups.map((group) => existingQueries[group.kind].error).find(Boolean) || propertiesQuery.error;

  const planned = groups.map((group) => {
    const plans = loadingExisting
      ? group.rows.map(() => null)
      : planRows(group.kind, group.rows, {
          existing: existingQueries[group.kind].data || [],
          properties: propertiesQuery.data || [],
        });
    const items = group.rows.map((row, index) => {
      const plan = plans[index];
      const selected = Boolean(plan && isImportable(plan) && (selection[row.id] ?? row.defaultSelected));
      return { row, plan, selected };
    });
    return { ...group, items };
  });

  const selectedItems = planned.flatMap((group) =>
    group.items.filter((item) => item.selected).map((item) => ({ ...item, kind: group.kind })),
  );

  async function addFiles(fileList) {
    const picked = Array.from(fileList || []).filter(
      (file) => /\.(csv|xlsx|xlsm)$/i.test(file.name) || file.type === "text/csv",
    );
    if (!picked.length) {
      toast({
        title: "Choose an Excel or CSV file",
        description: "Pick the workbook (.xlsx) or CSV files exported from it.",
        variant: "destructive",
      });
      return;
    }
    const loaded = [];
    for (const file of picked) {
      try {
        if (/\.xls[xm]$/i.test(file.name)) {
          // Each visible sheet of the workbook becomes its own entry.
          const { readXlsx } = await import("@/lib/xlsxReader");
          for (const sheet of readXlsx(await file.arrayBuffer())) {
            if (sheet.hidden || !sheet.rows.some((row) => row.some((cell) => cell.trim()))) continue;
            fileSeq += 1;
            loaded.push({
              id: `f${fileSeq}`,
              name: `${file.name} › ${sheet.name}`,
              label: sheet.name,
              rows: sheetRowsToLines(sheet.rows),
              forcedTab: null,
            });
          }
        } else {
          fileSeq += 1;
          loaded.push({
            id: `f${fileSeq}`,
            name: file.name,
            label: file.name,
            rows: parseCsvWithLines(await file.text()),
            forcedTab: null,
          });
        }
      } catch (error) {
        toast({ title: `Couldn't read ${file.name}`, description: error.message, variant: "destructive" });
      }
    }
    setResults(null);
    setImportError("");
    setFiles((prev) => [...prev, ...loaded]);
  }

  function removeFile(id) {
    setFiles((prev) => prev.filter((file) => file.id !== id));
  }

  function clearAll() {
    setFiles([]);
    setEdits({});
    setSelection({});
    setResults(null);
    setImportError("");
    setShowSecrets(false);
  }

  function setTab(id, tab) {
    setFiles((prev) => prev.map((file) => (file.id === id ? { ...file, forcedTab: tab || null } : file)));
  }

  function editField(rowId, key, value) {
    setEdits((prev) => ({ ...prev, [rowId]: { ...prev[rowId], [key]: value } }));
  }

  function toggleRow(rowId, checked) {
    setSelection((prev) => ({ ...prev, [rowId]: checked }));
  }

  function toggleGroup(group, checked) {
    setSelection((prev) => {
      const next = { ...prev };
      for (const item of group.items) {
        if (item.plan && isImportable(item.plan)) next[item.row.id] = checked;
      }
      return next;
    });
  }

  async function runImport() {
    if (!selectedItems.length) return;
    setImporting(true);
    setImportError("");
    const summary = [];
    try {
      const createdPropertyIds = {};
      const newProperties = propertiesToCreate(selectedItems);
      for (const property of newProperties) {
        const created = await createProperty({
          name: property.name,
          address: property.address,
          city: property.city,
          state: property.state,
          zipCode: property.zipCode,
          propertyType: "Single Family",
          units: 1,
          monthlyRent: property.monthlyRent || 0,
          notes: "Added from the spreadsheet import.",
        });
        createdPropertyIds[property.key] = created._id;
      }
      if (newProperties.length) {
        summary.push({ kind: "properties", created: newProperties.length, updated: 0 });
      }

      for (const kind of KIND_ORDER) {
        const items = selectedItems.filter((item) => item.kind === kind);
        if (!items.length) continue;
        const totals = await runImporter(kind, buildImportPayload(kind, items, createdPropertyIds));
        summary.push({ kind, ...totals });
      }

      setSelection({});
      const created = summary.reduce((sum, item) => sum + item.created, 0);
      const updated = summary.reduce((sum, item) => sum + item.updated, 0);
      toast({
        title: "Import complete",
        description: `${created} added, ${updated} updated.`,
      });
    } catch (error) {
      setImportError(
        `${error?.message || "Import failed."} Anything listed below was saved — fix the issue and press Import again; rows already saved show as "Already there".`,
      );
    } finally {
      setResults(summary);
      setImporting(false);
      await queryClient.invalidateQueries();
    }
  }

  const resultLabel = (kind) => (kind === "properties" ? "Properties" : KIND_LABELS[kind]);
  const resultLink = (kind) => (kind === "properties" ? "/properties" : KIND_DESTINATIONS[kind]);

  return (
    <PageLayout
      title="Import spreadsheet"
      eyebrow="Resources"
      subtitle="Keep your Business Transaction workbook and your workspace in step."
    >
      <div className="space-y-6 pb-24">
        <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div className="space-y-1">
            <p className="font-medium text-emerald-700 dark:text-emerald-300">Stays private to your account</p>
            <p className="text-muted-foreground">
              Workbook data only goes to your signed-in workspace — never to the public website. Passwords from
              the Credentials tab land in your Secure vault.
            </p>
          </div>
        </div>

        <OneDriveSync />

        <div className="space-y-1 pt-2">
          <h2 className="text-lg font-semibold">Or import a file by hand</h2>
          <p className="text-sm text-muted-foreground">
            Files are read here in your browser and nothing is saved until you press Import.
          </p>
        </div>

        <Card>
          <CardContent className="space-y-5 p-5">
            <div
              className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                dragging ? "border-primary bg-primary/5" : "border-border"
              }`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                addFiles(event.dataTransfer.files);
              }}
            >
              <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
              <div className="space-y-1">
                <p className="font-medium">Drop your workbook (.xlsx) or CSV files here</p>
                <p className="text-sm text-muted-foreground">
                  Contractors Info, Credentials, Expenses, Insurance and any per-property tabs.
                </p>
              </div>
              <Button onClick={() => inputRef.current?.click()} className="gap-2">
                <Upload className="h-4 w-4" />
                Choose files
              </Button>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xlsm,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                multiple
                className="hidden"
                onChange={(event) => {
                  addFiles(event.target.files);
                  event.target.value = "";
                }}
              />
            </div>

            <ol className="grid gap-2 text-sm text-muted-foreground md:grid-cols-3">
              <li>
                <span className="font-medium text-foreground">1.</span> In OneDrive or Excel, download the workbook
                (<strong>File → Save a copy → Download</strong>) — or export tabs as CSV.
              </li>
              <li>
                <span className="font-medium text-foreground">2.</span> Choose the file here. Each tab is recognised
                automatically.
              </li>
              <li>
                <span className="font-medium text-foreground">3.</span> Check the preview, fill in anything marked{" "}
                <em>Needs info</em>, then press <strong>Import</strong>. Re-importing later only adds what's new.
              </li>
            </ol>

            {parsedFiles.length ? (
              <div className="space-y-2">
                {parsedFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex flex-col gap-2 rounded-lg border p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate font-medium">{file.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="sr-only" htmlFor={`tab-${file.id}`}>
                        Tab type for {file.name}
                      </label>
                      <select
                        id={`tab-${file.id}`}
                        value={file.forcedTab || file.tab || ""}
                        onChange={(event) => setTab(file.id, event.target.value)}
                        className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                      >
                        <option value="">Choose what this tab is…</option>
                        {Object.entries(TAB_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => removeFile(file.id)}
                        aria-label={`Remove ${file.name}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={clearAll}>
                    Clear everything
                  </Button>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {loadError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            Couldn't load your current records to compare against: {loadError.message}
          </div>
        ) : null}

        {importError ? (
          <div className="flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{importError}</p>
          </div>
        ) : null}

        {results?.length ? (
          <Card>
            <CardContent className="space-y-3 p-5">
              <div className="flex items-center gap-2 font-medium">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                Saved to your workspace
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {results.map((item) => (
                  <Link
                    key={item.kind}
                    to={resultLink(item.kind)}
                    className="flex items-center justify-between rounded-lg border p-3 text-sm hover:bg-muted/50"
                  >
                    <span>
                      <span className="font-medium">{resultLabel(item.kind)}</span>
                      <span className="block text-muted-foreground">
                        {item.created} added{item.updated ? `, ${item.updated} updated` : ""}
                      </span>
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {planned.map((group) => (
          <KindPreview
            key={group.kind}
            group={group}
            loading={loadingExisting}
            showSecrets={showSecrets}
            onToggleSecrets={() => setShowSecrets((value) => !value)}
            onEdit={editField}
            onToggleRow={toggleRow}
            onToggleGroup={toggleGroup}
          />
        ))}

        {parsedFiles.length && !groups.length ? (
          <p className="text-sm text-muted-foreground">
            Pick what each file is from the menu above to see a preview.
          </p>
        ) : null}

        {groups.length ? (
          <div className="sticky bottom-4 z-10 flex flex-col gap-3 rounded-2xl border bg-background/95 p-4 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm">
              {loadingExisting ? (
                <span className="text-muted-foreground">Comparing with what's already in your account…</span>
              ) : (
                <>
                  <span className="font-semibold tabular-nums">{selectedItems.length}</span> row
                  {selectedItems.length === 1 ? "" : "s"} ticked to import
                </>
              )}
            </p>
            <Button
              onClick={runImport}
              disabled={importing || loadingExisting || Boolean(loadError) || !selectedItems.length}
              className="gap-2"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {importing ? "Importing…" : "Import"}
            </Button>
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}

function KindPreview({ group, loading, showSecrets, onToggleSecrets, onEdit, onToggleRow, onToggleGroup }) {
  const [showSame, setShowSame] = useState(false);
  const fields = FIELD_SPECS[group.kind];
  const counts = { new: 0, update: 0, same: 0, invalid: 0 };
  for (const item of group.items) {
    if (item.plan) counts[item.plan.status] += 1;
  }
  const importable = group.items.filter((item) => item.plan && isImportable(item.plan));
  const allSelected = importable.length > 0 && importable.every((item) => item.selected);
  const someSelected = importable.some((item) => item.selected);
  const visible = showSame ? group.items : group.items.filter((item) => item.plan?.status !== "same");

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">
              {KIND_LABELS[group.kind]}{" "}
              <span className="text-sm font-normal text-muted-foreground">· {group.rows.length} rows</span>
            </h2>
            {!loading ? (
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(counts)
                  .filter(([, count]) => count > 0)
                  .map(([status, count]) => (
                    <Badge key={status} className={STATUS[status].className} variant="secondary">
                      {count} {STATUS[status].label.toLowerCase()}
                    </Badge>
                  ))}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {group.kind === "vault" ? (
              <Button variant="outline" size="sm" className="gap-2" onClick={onToggleSecrets}>
                {showSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showSecrets ? "Hide passwords" : "Show passwords"}
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" asChild>
              <Link to={KIND_DESTINATIONS[group.kind]} className="gap-1">
                Open {KIND_LABELS[group.kind]}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        {!loading && counts.same > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>
              {visible.length === 0
                ? "Everything in this tab is already in your workspace."
                : `${counts.same} row${counts.same === 1 ? " is" : "s are"} already in your workspace.`}
            </span>
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setShowSame((value) => !value)}>
              {showSame ? "Hide them" : `Show ${counts.same} already there`}
            </Button>
          </div>
        ) : null}

        {visible.length ? (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-max text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-10 p-2">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    disabled={loading || !importable.length}
                    onChange={(checked) => onToggleGroup(group, checked)}
                    label={`Select all ${KIND_LABELS[group.kind]}`}
                  />
                </th>
                <th className="p-2">Status</th>
                {fields.map((field) => (
                  <th key={field.key} className="p-2 font-medium">
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(({ row, plan, selected }) => (
                <PreviewRow
                  key={row.id}
                  row={row}
                  plan={plan}
                  selected={selected}
                  fields={fields}
                  showSecrets={showSecrets}
                  onEdit={onEdit}
                  onToggle={onToggleRow}
                />
              ))}
            </tbody>
          </table>
        </div>
        ) : null}

        {group.skipped.length ? (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              {group.skipped.length} line{group.skipped.length === 1 ? "" : "s"} left out
            </summary>
            <ul className="mt-2 space-y-1 text-muted-foreground">
              {group.skipped.map((item) => (
                <li key={`${item.fileName}-${item.line}`}>
                  Line {item.line}: {item.reason}
                  {item.preview && group.kind !== "vault" ? (
                    <span className="text-xs"> — {item.preview}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Checkbox({ checked, indeterminate = false, disabled, onChange, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      aria-label={label}
      className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
    />
  );
}

const inputClass =
  "h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function PreviewRow({ row, plan, selected, fields, showSecrets, onEdit, onToggle }) {
  const status = plan ? STATUS[plan.status] : null;
  const changed = new Set(Object.keys(plan?.changes || {}));
  const muted = plan?.status === "same";
  const propertyHint = plan?.property && PROPERTY_HINT[plan.property.action]?.(plan.property);

  return (
    <tr className={`border-t align-top ${muted ? "opacity-60" : ""}`}>
      <td className="p-2">
        <Checkbox
          checked={selected}
          disabled={!plan || !isImportable(plan)}
          onChange={(checked) => onToggle(row.id, checked)}
          label={`Import line ${row.line}`}
        />
      </td>
      <td className="w-56 max-w-[14rem] space-y-1 p-2">
        {status ? (
          <Badge variant="secondary" className={status.className}>
            {status.label}
          </Badge>
        ) : (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
        <p className="text-xs text-muted-foreground">Line {row.line}</p>
        {plan?.problems?.map((problem) => (
          <p key={problem} className="text-xs text-amber-700 dark:text-amber-300">
            {problem}
          </p>
        ))}
        {row.warnings.map((warning) => (
          <p key={warning} className="text-xs text-muted-foreground">
            {warning}
          </p>
        ))}
      </td>
      {fields.map((field) => {
        const value = row.data[field.key] ?? "";
        const label = `${field.label}, line ${row.line}`;
        const highlight = changed.has(field.key) ? "ring-1 ring-sky-400 bg-sky-500/5" : "";
        const width = field.wide ? "min-w-[16rem]" : field.type === "date" ? "min-w-[9.5rem]" : "min-w-[8.5rem]";
        let control;
        if (field.type === "select") {
          control = (
            <select
              aria-label={label}
              value={value}
              onChange={(event) => onEdit(row.id, field.key, event.target.value)}
              className={`${inputClass} ${highlight}`}
            >
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        } else {
          control = (
            <input
              aria-label={label}
              type={
                field.type === "date" ? "date" : field.type === "secret" && !showSecrets ? "password" : "text"
              }
              inputMode={field.type === "money" ? "decimal" : undefined}
              autoComplete={field.type === "secret" ? "new-password" : "off"}
              spellCheck={false}
              value={value}
              onChange={(event) => onEdit(row.id, field.key, event.target.value)}
              className={`${inputClass} ${highlight}`}
            />
          );
        }
        return (
          <td key={field.key} className={`p-2 ${width}`}>
            {control}
            {field.key === "propertyLabel" && propertyHint ? (
              <p className="mt-1 text-xs text-muted-foreground">{propertyHint}</p>
            ) : null}
          </td>
        );
      })}
    </tr>
  );
}
